import type { TFunction } from 'i18next';

import {
    HeatingUsageProfileKey,
    InvestmentBasisKey,
    PvEstimateUnavailableReason,
    PvRoofSource,
    RangeDto,
} from './ensan-steckbrief.dto';
import { variantFieldByKey } from './steckbrief-variant-fields';

/**
 * Client-side rendering of the item-by-item investment estimate (GEN-780).
 * The `/calculate` response is language-agnostic (ADR-018): basis/status
 * CODES plus RAW numbers — this module owns the labels, units and the
 * per-locale number formatting (de "2.600.000", en "2,600,000"). The rates,
 * the quantities and the 100,000 € rounding of the totals stay backend-owned;
 * nothing here recomputes a cost.
 */

/** i18next key of a total the engine could not give — de "k. A." like the print, en "N/A". */
export const INVESTMENT_NOT_AVAILABLE_KEY = 'N/A';

/**
 * The Variant form key whose EFFECTIVE value is the quantity of a basis — the
 * "missing input" hint of a QUANTITY_UNKNOWN line names that field by its form
 * label, so ops know exactly which input to fill. `FLAT_RATE` has no input: a
 * flat amount is always PRICED (quantity 1), it can never be QUANTITY_UNKNOWN.
 */
export const INVESTMENT_BASIS_FORM_KEYS: Partial<Record<InvestmentBasisKey, string>> = {
    FOOTPRINT_AREA: 'footprintArea',
    FACADE_SURFACE_AREA: 'facadeSurfaceArea',
    WINDOW_SURFACE_AREA: 'windowSurfaceArea',
    NET_FLOOR_AREA: 'netFloorArea',
    // The hourly heat consumption is backend-only (derived during the
    // calculation), so the input to fill is the heat consumption it comes from.
    HEAT_PUMP_POWER: 'heatConsumption',
};

/** i18next key of the quantity cell of a FLAT_RATE line — de "pauschal", no number. */
export const FLAT_RATE_QUANTITY_KEY = 'flat rate';

/** Physical unit symbols of the metric bases — locale-neutral, never translated. */
const BASIS_UNIT_SYMBOLS: Partial<Record<InvestmentBasisKey, string>> = {
    FOOTPRINT_AREA: 'm²',
    FACADE_SURFACE_AREA: 'm²',
    WINDOW_SURFACE_AREA: 'm²',
    NET_FLOOR_AREA: 'm²',
    HEAT_PUMP_POWER: 'kW',
    // GEN-799: the PV line's quantity is the PV Calculator design's peak power —
    // no form input behind it, so no INVESTMENT_BASIS_FORM_KEYS entry.
    PV_PEAK_POWER: 'kWp',
};

/** Fraction digits of quantities and unit rates (e.g. 185,15 m², 27,3 €/m²). */
const DETAIL_MAX_FRACTION_DIGITS = 2;

function localeOf(language: string | undefined): string {
    return typeof language === 'string' && language.startsWith('de') ? 'de-DE' : 'en-US';
}

/** Locale-grouped number: de "2.600.000" / en "2,600,000", at most `maxFractionDigits` decimals. */
export function formatInvestmentNumber(language: string | undefined, value: number, maxFractionDigits = 0): string {
    return value.toLocaleString(localeOf(language), { maximumFractionDigits: maxFractionDigits });
}

/**
 * "low – high €" in whole euros — equal bounds collapse to one number (the
 * fixed-rate ventilation costs "50.000 €"); null for a missing range (the
 * caller renders its own "n/a").
 */
export function formatEuroRange(language: string | undefined, range: RangeDto | null | undefined): string | null {
    if (!range) return null;
    const low = formatInvestmentNumber(language, range.low);
    if (range.low === range.high) return `${low} €`;
    return `${low} – ${formatInvestmentNumber(language, range.high)} €`;
}

/**
 * The quantity of a line with its unit ("1.000 m²", "200 kW") — a FLAT_RATE
 * line reads "pauschal" instead of its technical quantity 1.
 */
export function formatInvestmentQuantity(
    t: TFunction,
    language: string | undefined,
    basis: InvestmentBasisKey | null,
    quantity: number | null,
): string {
    if (basis === 'FLAT_RATE') return t(FLAT_RATE_QUANTITY_KEY);
    if (quantity === null) return '–';
    const formatted = formatInvestmentNumber(language, quantity, DETAIL_MAX_FRACTION_DIGITS);
    const unit = basis === null ? undefined : BASIS_UNIT_SYMBOLS[basis];
    return unit === undefined ? formatted : `${formatted} ${unit}`;
}

/**
 * The unit-rate range of a line ("170 – 221 €/m²"); a flat amount has no unit
 * ("50.000 €"), and equal bounds collapse to one number — the ventilation's
 * fixed rate is low = high.
 */
export function formatUnitCostRange(
    language: string | undefined,
    basis: InvestmentBasisKey | null,
    low: number | null,
    high: number | null,
): string {
    if (low === null || high === null) return '–';
    const symbol = basis === null ? undefined : BASIS_UNIT_SYMBOLS[basis];
    const unit = symbol === undefined ? '€' : `€/${symbol}`;
    const lowText = formatInvestmentNumber(language, low, DETAIL_MAX_FRACTION_DIGITS);
    if (low === high) return `${lowText} ${unit}`;
    const highText = formatInvestmentNumber(language, high, DETAIL_MAX_FRACTION_DIGITS);
    return `${lowText} – ${highText} ${unit}`;
}

/**
 * The form-field label of the input a QUANTITY_UNKNOWN line is missing —
 * through the field model, so it reads exactly like the control to fill in;
 * an unknown (future) basis renders its raw code.
 */
export function missingInvestmentInputLabel(t: TFunction, basis: InvestmentBasisKey | null): string {
    if (basis === null) return '–';
    const formKey = INVESTMENT_BASIS_FORM_KEYS[basis];
    const labelKey = formKey === undefined ? undefined : variantFieldByKey(formKey)?.labelKey;
    return labelKey === undefined ? basis : t(labelKey);
}

/**
 * i18next label keys of the DIN V 18599-10 Tabelle 5 usage profiles (GEN-780)
 * the derived hourly heat consumption divides by — keyed by the stable backend
 * `HeatingUsageProfile` enum name. The de catalog values are the table's
 * "Nutzungen" names with their "Lfd.-Nr." (the backend enum's dinNumber +
 * dinName); the en texts translate them. The yearly heating hours are NOT
 * mirrored here: they travel as the `heatingHours` assumption param.
 */
export const HEATING_USAGE_PROFILE_LABEL_KEYS: Record<HeatingUsageProfileKey, string> = {
    OFFICE: 'No. 1 Single office',
    MEETING: 'No. 4 Meeting, conference, seminar',
    CLASSROOM: 'No. 8 Classroom (school), group room (kindergarten)',
    LECTURE_HALL: 'No. 9 Lecture hall, auditorium',
    BED_ROOM: 'No. 10 Patient bedroom',
    HOTEL_ROOM: 'No. 11 Hotel room',
    OTHER_LOUNGE: 'No. 17 Other occupied rooms',
    INDUSTRIAL_HALL: 'No. 22.2 Commercial and industrial halls – medium-heavy work',
    AUDIENCE_AREA: 'No. 23 Audience area (theatres and event venues)',
    MUSEUM: 'No. 27 Exhibition rooms and museums with conservation requirements',
    LIBRARY_READING_ROOM: 'No. 28 Library – reading room',
    SPORTS_HALL: 'No. 31 Sports hall (without spectator area)',
    LABORATORY: 'No. 36 Laboratory',
    MEDICAL_PRACTICE: 'No. 40 Medical and therapy practices',
};

/** UI label of a usage-profile code in the active language (raw code for an unknown, future profile). */
export function heatingUsageProfileLabel(t: TFunction, value: string): string {
    const key = HEATING_USAGE_PROFILE_LABEL_KEYS[value as HeatingUsageProfileKey];
    return key === undefined ? value : t(key);
}

// ---------------------------------------------------------------------------
// PV investment from the PV Calculator (GEN-799)
// ---------------------------------------------------------------------------

/**
 * i18next label keys of the reasons a PV estimate is not READY (backend
 * `PvEstimateUnavailableReason`) — rendered in parentheses after the "PV
 * Calculator failed" line text and in the PV_INVESTMENT_NOT_AVAILABLE
 * assumption.
 */
export const PV_ESTIMATE_REASON_LABEL_KEYS: Record<PvEstimateUnavailableReason, string> = {
    NO_FOOTPRINT: 'no building footprint available',
    NO_ROOF_POTENTIAL: 'no roof potential found',
    NO_SUITABLE_ROOF: 'no suitable roof area',
    CALCULATOR_REJECTED: 'the PV Calculator rejected the request',
    CALCULATOR_UNAVAILABLE: 'the PV Calculator was unavailable',
    DISABLED: 'PV estimate disabled',
    LEGACY: 'variant saved before the PV estimate existed',
};

/** UI label of a PV-estimate reason in the active language (raw code for an unknown, future reason). */
export function pvEstimateReasonLabel(t: TFunction, value: string): string {
    const key = PV_ESTIMATE_REASON_LABEL_KEYS[value as PvEstimateUnavailableReason];
    return key === undefined ? value : t(key);
}

/**
 * The reasons the advisor can act on with "Refresh PV estimate" (the
 * synchronous POST refresh, then a recalculation). DISABLED and LEGACY offer
 * nothing to do.
 */
export const PV_ESTIMATE_RETRYABLE_REASONS: readonly PvEstimateUnavailableReason[] = [
    'CALCULATOR_UNAVAILABLE',
    'CALCULATOR_REJECTED',
    'NO_ROOF_POTENTIAL',
    'NO_SUITABLE_ROOF',
    'NO_FOOTPRINT',
];

/** True when the refresh action makes sense for a not-READY estimate's reason. */
export function isPvEstimateRetryable(reason: string | null | undefined): boolean {
    return reason != null && (PV_ESTIMATE_RETRYABLE_REASONS as readonly string[]).includes(reason);
}

/**
 * Display names of the roof-geometry sources (backend `PvRoofSource`) —
 * product names, locale-neutral like the unit symbols, never translated.
 */
export const PV_ROOF_SOURCE_LABELS: Record<PvRoofSource, string> = {
    GOOGLE_SOLAR: 'Google Solar',
    ADMI_SOLAR: 'admi Solar',
};

/** Roof-source label (raw code for an unknown, future source). */
export function pvRoofSourceLabel(value: string): string {
    return PV_ROOF_SOURCE_LABELS[value as PvRoofSource] ?? value;
}

/**
 * A backend ISO instant as a per-locale date + time ("29.09.2026, 14:05" /
 * "Sep 29, 2026, 2:05 PM") — the PV estimate's `computedAt`. An unparsable
 * value renders verbatim rather than "Invalid Date".
 */
export function formatInstant(language: string | undefined, value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(localeOf(language), { dateStyle: 'medium', timeStyle: 'short' });
}
